import type {ClContract, ClMatchBasis, ClTransaction, ClTxChannel} from "./contractLinkageData";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatCompactCurrency(value: number, currency: string): string {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            notation: 'compact',
            maximumFractionDigits: value >= 1_000_000 ? 2 : 0,
        }).format(value);
    } catch {
        return `${currency} ${value.toLocaleString('en-US')}`;
    }
}

export function formatFullCurrency(value: number, currency: string): string {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        }).format(value);
    } catch {
        return `${currency} ${value.toLocaleString('en-US')}`;
    }
}

export function formatNumber(value: number): string {
    return value.toLocaleString('en-US');
}

export function formatPct(value: number, digits = 1): string {
    return `${(value * 100).toFixed(digits)}%`;
}

/** "12 Mar 2026" from an ISO date. */
export function formatDate(isoDate: string): string {
    const [y, m, d] = isoDate.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'});
}

/** "Jan 2024 – Dec 2026" period label for a contract. */
export function formatPeriod(c: ClContract): string {
    const f = (isoDate: string) => {
        const [y, m] = isoDate.split('-').map(Number);
        return new Date(y, m - 1, 1).toLocaleDateString('en-GB', {month: 'short', year: 'numeric'});
    };
    return `${f(c.startDate)} – ${f(c.endDate)}`;
}

// ---------------------------------------------------------------------------
// Confidence tiers
// ---------------------------------------------------------------------------

export type ClTier = 'high' | 'medium' | 'low';

export const TIER_LABEL: Record<ClTier, string> = {
    high: 'High',
    medium: 'Medium',
    low: 'Low',
};

export function tierOfConfidence(confidence: number): ClTier {
    if (confidence >= 85) return 'high';
    if (confidence >= 60) return 'medium';
    return 'low';
}

/** Background / text colours for a confidence pill (Mithra palette). */
export function tierColors(tier: ClTier): {bg: string; fg: string} {
    switch (tier) {
        case 'high':
            return {bg: 'rgba(8, 165, 122, 0.12)', fg: '#08A57A'};
        case 'medium':
            return {bg: 'rgba(250, 169, 21, 0.14)', fg: '#cc880c'};
        case 'low':
            return {bg: 'rgba(255, 52, 53, 0.10)', fg: '#d32f2f'};
    }
}

// ---------------------------------------------------------------------------
// Match basis
// ---------------------------------------------------------------------------

export const BASIS_LABEL: Record<ClMatchBasis, string> = {
    po_reference: 'PO reference',
    line_terms: 'Rates & terms',
    date_window: 'Period & pattern',
    supplier_period: 'Supplier fallback',
};

export const BASIS_HELP: Record<ClMatchBasis, string> = {
    po_reference: 'The invoice references a purchase order issued under this contract — the strongest evidence.',
    line_terms: 'Line prices and payment terms match the contracted rates and terms.',
    date_window: 'The invoice falls inside the contract period and fits its recurring invoicing pattern, but carries no PO or line-item evidence.',
    supplier_period: 'The contract terms were unclear, so all supplier spend within the contract period was allocated with low confidence.',
};

// ---------------------------------------------------------------------------
// Transaction channel (how indirect spend reached the company)
// ---------------------------------------------------------------------------

export const CHANNEL_LABEL: Record<ClTxChannel, string> = {
    marketplace: 'Marketplace',
    payment_provider: 'Payment provider',
    employee_expense: 'Employee expense',
};

export const CHANNEL_HELP: Record<ClTxChannel, string> = {
    marketplace: 'Bought via a marketplace or distributor — the invoice names the marketplace, the real vendor only appears at line level.',
    payment_provider: 'Settled through a payment provider — the invoice names the provider, not the supplier who did the work.',
    employee_expense: 'Paid privately by an employee and reimbursed — the supplier field carries the employee’s name.',
};

// ---------------------------------------------------------------------------
// Review decisions (human-in-the-loop), persisted in localStorage
// ---------------------------------------------------------------------------

export type ClDecision =
    | {action: 'accept'}
    | {action: 'assign'; contractId: string}
    | {action: 'unlink'};

export type ClDecisions = Record<string, ClDecision>; // transaction id -> decision

const DECISIONS_STORAGE_KEY = 'cl-review-decisions';

export function loadDecisions(): ClDecisions {
    try {
        return JSON.parse(localStorage.getItem(DECISIONS_STORAGE_KEY) ?? '{}');
    } catch {
        return {};
    }
}

export function saveDecisions(decisions: ClDecisions) {
    try {
        localStorage.setItem(DECISIONS_STORAGE_KEY, JSON.stringify(decisions));
    } catch {
        // Storage unavailable (private mode); decisions stay session-only.
    }
}

// ---------------------------------------------------------------------------
// Effective link & review status
// ---------------------------------------------------------------------------

export type ClStatus = 'pending' | 'accepted' | 'relinked' | 'unlinked' | 'unmatched';

export const STATUS_LABEL: Record<ClStatus, string> = {
    pending: 'To review',
    accepted: 'Accepted',
    relinked: 'Relinked',
    unlinked: 'Unlinked',
    unmatched: 'No link',
};

export function statusColors(status: ClStatus): {bg: string; fg: string} {
    switch (status) {
        case 'pending':
            return {bg: 'rgba(64, 136, 248, 0.12)', fg: '#2f6fd1'};
        case 'accepted':
            return {bg: 'rgba(8, 165, 122, 0.12)', fg: '#08A57A'};
        case 'relinked':
            return {bg: 'rgba(123, 97, 255, 0.12)', fg: '#6a4fd8'};
        case 'unlinked':
            return {bg: 'rgba(68, 71, 78, 0.08)', fg: '#44474E'};
        case 'unmatched':
            return {bg: 'rgba(250, 169, 21, 0.14)', fg: '#cc880c'};
    }
}

/** The contract a transaction is effectively linked to, after the user's decision. */
export function effectiveContractId(tx: ClTransaction, decision: ClDecision | undefined): string | null {
    if (!decision) return tx.aiContractId;
    switch (decision.action) {
        case 'accept':
            return tx.aiContractId;
        case 'assign':
            return decision.contractId;
        case 'unlink':
            return null;
    }
}

export function statusOf(tx: ClTransaction, decision: ClDecision | undefined): ClStatus {
    if (!decision) return tx.aiContractId ? 'pending' : 'unmatched';
    switch (decision.action) {
        case 'accept':
            return 'accepted';
        case 'assign':
            return 'relinked';
        case 'unlink':
            return 'unlinked';
    }
}

/** A transaction enriched with its review status and effective link, for display. */
export type ClReviewTx = ClTransaction & {
    status: ClStatus;
    tier: ClTier;
    effectiveContractId: string | null;
    effectiveContractName: string | null;
    aiContractName: string | null;
};

// ---------------------------------------------------------------------------
// Workload filter (used for review-progress KPIs)
// ---------------------------------------------------------------------------

export type ClFilterKey = 'pending' | 'low' | 'unmatched' | 'reviewed';

export function matchesFilter(tx: ClReviewTx, filter: ClFilterKey): boolean {
    switch (filter) {
        case 'pending':
            return tx.status === 'pending';
        case 'low':
            return tx.status === 'pending' && tx.tier === 'low';
        case 'unmatched':
            return tx.status === 'unmatched';
        case 'reviewed':
            return tx.status === 'accepted' || tx.status === 'relinked' || tx.status === 'unlinked';
    }
}

// ---------------------------------------------------------------------------
// Portfolio filter (status cards above the explorer filter the contract list)
// ---------------------------------------------------------------------------

export type ClPortfolioFilter = 'active' | 'expiring' | 'expired' | 'attention';

// ---------------------------------------------------------------------------
// Contract edits (user corrections to the extracted metadata), in localStorage
// ---------------------------------------------------------------------------

export type ClContractPatch = Partial<Omit<ClContract, 'id'>>;

/** contract id -> user-edited fields, overriding the extracted values. */
export type ClContractEdits = Record<string, ClContractPatch>;

const CONTRACT_EDITS_STORAGE_KEY = 'cl-contract-edits';

export function loadContractEdits(): ClContractEdits {
    try {
        return JSON.parse(localStorage.getItem(CONTRACT_EDITS_STORAGE_KEY) ?? '{}');
    } catch {
        return {};
    }
}

export function saveContractEdits(edits: ClContractEdits) {
    try {
        localStorage.setItem(CONTRACT_EDITS_STORAGE_KEY, JSON.stringify(edits));
    } catch {
        // Storage unavailable (private mode); edits stay session-only.
    }
}

/** True when an ISO date falls inside the contract validity. */
export function withinContractPeriod(isoDate: string, c: ClContract): boolean {
    return isoDate >= c.startDate && isoDate <= c.endDate;
}
